<i18n>
{
	"en": {
		"title": "Select stretch",
		"desc": "Which stretch of the order ({orderNumbers}) should be assigned to trip {tripNumber}?",
		"fromTripStart": "From Trip Start",
		"fromPickupLocation": "From Pickup Location",
		"toTripEnd": "To Trip End",
		"toDeliveryLocation": "To Delivery Location",
		"addToTrip": "Add to Trip",
		"multipleLocations": "Multiple Locations",
		"plannedETD": "Planned Departure",
		"plannedETA": "Planned Arrival",
		"selectDate": "Select date",
		"outOfRangeWarning": "Selected date is outside the trip planned date range.",
		"customFromLocation": "Custom Pickup Location",
		"customToLocation": "Custom Unloading Location",
		"useCustomFromLocation": "Use custom pickup location",
		"useCustomToLocation": "Use custom unloading location",
		"fromViaLocation": "From Via Location",
		"toViaLocation": "To Via Location",
		"ordersEtdWarning": "| Order {orders} expect pickup after the trip ends! | Orders {orders} expect pickup after trip ends!",
		"ordersEtaWarning": "| Order {orders} expect delivery before the trip starts! | Orders {orders} expect delivery before the trip starts!"
	},
	"no": {
		"title": "Velg strekninger",
		"desc": "Hvilke deler av Oppdraget ({orderNumbers}) skal legges til på tur {tripNumber}?",
		"fromTripStart": "Fra turstart",
		"fromPickupLocation": "Fra hentested",
		"toTripEnd": "Til turslutt",
		"toDeliveryLocation": "Til leveringssted",
		"addToTrip": "Legg til på tur",
		"multipleLocations": "Flere steder",
		"plannedETD": "Planlagt Avgang",
		"plannedETA": "Planlagt Ankomst",
		"selectDate": "Velg dato",
		"outOfRangeWarning": "Selected date is outside the trip planned date range.",
		"customFromLocation": "Egendefinert hentested",
		"customToLocation": "Egendefinert leveringssted",
		"useCustomFromLocation": "Bruk egendefinert hentested",
		"useCustomToLocation": "Bruk egendefinert leveringssted",
		"fromViaLocation": "Fra via-sted",
		"toViaLocation": "Til via-sted",
		"ordersEtdWarning": "Oppdrag {orders} forventer henting etter turens slutt!",
		"ordersEtaWarning": "Oppdrag {orders} forventer levering før turens start!"
	}
}
</i18n>
<template>
	<t-dialog
		:isShown="isShown"
		:title="$t('title')"
		icon="order"
		iconColor="blue"
		width="600px"
		@close="onClose"
		@shown="processShown"
	>
		<div class="dialog-top">
			{{ $t('desc', {orderNumbers, tripNumber}) }}
		</div>
		<div class="dialog-mid">
			<div class="dialog-left">
				<t-form-group :title="$t('plannedETD')">
					<div style="display:flex; margin-bottom:10px">
						<t-date-picker
							:placeholder="$t('selectDate')"
							:value="formatDateObject(etd)"
							:dateFormat="displayDateFormat"
							style="margin-right: 5px;"
							@input="changeEtd({dateValue: $event, hasTime: null})"
						/>
						<t-time-picker
							:value="etdTime"
							@input="changeEtd({timeValue: $event, hasTime: Boolean($event)})"
						/>
					</div>
				</t-form-group>
				<div v-if="ordersDateTimeValidation.isInvalid" class="orders-date-time-warning">
					{{ordersDateTimeWarningMessages.etd}}
					<div
						v-if="ordersDateTimeWarningMessages.etd && ordersDateTimeWarningMessages.eta"
						style="height: 8px;"
					/>
					{{ordersDateTimeWarningMessages.eta}}
				</div>

				<t-form-group :title="$t('plannedETA')">
					<div style="display:flex">
						<t-date-picker
							:placeholder="$t('selectDate')"
							:value="formatDateObject(eta)"
							:dateFormat="displayDateFormat"
							style="margin-right: 5px;"
							@input="changeEta({dateValue: $event, hasTime: null})"
						/>

						<t-time-picker
							:value="etaTime"
							@input="changeEta({timeValue: $event, hasTime: Boolean($event)})"
						/>
					</div>
				</t-form-group>
				<div class="custom-switches">
					<t-switch
						v-model="allowCustomFrom"
						:label="$t('useCustomFromLocation')"
						@change="customFromToggle"
					/>
					<t-switch
						v-model="allowCustomTo"
						:label="$t('useCustomToLocation')"
						@change="customToToggle"
					/>
				</div>
			</div>
			<div class="dialog-right">
				<t-stretch-select
					v-if="fromLocations && toLocations"
					:fromLocations="fromLocations"
					:fromIndex="from"
					:toLocations="toLocations"
					:toIndex="to"
					:allowCustomFrom="allowCustomFrom"
					:allowCustomTo="allowCustomTo"
					@changeFromLocation="changeFromLocation"
					@changeToLocation="changeToLocation"
				>
					<template v-slot:customFromDetails="{location, index}">
						<t-select
							:items="location.items"
							:selectedItem="location.selectedItem"
							width="100%"
							displayName="name"
							relative
							@change="onChangeFromViaPlace(index, $event)"
						>
							<template #optionItemValue="slotProps">
								{{slotProps.item.name}}
							</template>
						</t-select>
					</template>
					<template v-slot:customToDetails="{location, index}">
						<t-select
							:items="location.items"
							:selectedItem="location.selectedItem"
							width="100%"
							displayName="name"
							relative
							@change="onChangeToViaPlace(index, $event)"
						>
							<template #optionItemValue="slotProps">
								{{slotProps.item.name}}
							</template>
						</t-select>
					</template>

					<template v-slot:customFrom>
						<GlobalAddressInput
							:title="$t('customFromLocation')"
							:value="customFromLocation"
							@change="onCustomFromSelected"
						/>
					</template>
					<template v-slot:customTo>
						<GlobalAddressInput
							:title="$t('customToLocation')"
							:value="customToLocation"
							@change="onCustomToSelected"
						/>
					</template>
				</t-stretch-select>
			</div>
		</div>
		<template v-slot:footer>
			<t-button
				:disabled="disableSubmit"
				appearance="primary"
				class="timpex-modal-button"
				width="100%"
				@click="addToTrip"
			>
				{{$t('addToTrip')}}
			</t-button>
		</template>
	</t-dialog>
</template>
<script>
import {
	displayDateFormat, storageDateFormat, createDateObject, displayDateFormatter, formatArray,
} from '@/core';
import moment from 'moment';
import deepCopy from 'deepcopy';
import { validateDateTimeForOrdersInTrip } from '../validation/ordersInTrip';
import TStretchSelect from '@/components/evergreen/timpex/inputs/TStretchSelect';
import GlobalAddressInput from '@/modules/globalAddresses/GlobalAddressInput';

export default {
	name: 'AddOrderToTripLocationsSelect',
	components: { GlobalAddressInput, TStretchSelect },
	props: {
		isShown: Boolean,
		selectedTrip: Object,
		orders: Array,
		disableSubmit: Boolean,
	},
	data() {
		return {
			fromLocations: [],
			toLocations: [],
			customFromLocation: {},
			customToLocation: {},
			from: 0,
			to: 0,
			etd: {
				date: '',
				hasTime: false,
				timeZone: 'Europe/Oslo',
				epoch: ''
			},
			eta: {
				date: '',
				hasTime: false,
				timeZone: 'Europe/Oslo',
				epoch: ''
			},
			allowCustomFrom: false,
			allowCustomTo: false,
			defaultStartIndex: 0,
			defaultEndIndex: 0,
		};
	},
	computed: {
		displayDateFormat() {
			return displayDateFormat;
		},
		etdOutOfRange() {
			if (!this.selectedTrip || !this.selectedTrip.etd) return true;
			return this.etd.date < this.selectedTrip.etd.date || (this.selectedTrip.eta && this.etd.date > this.selectedTrip.eta.date);
		},
		etaOutOfRange() {
			if (!this.selectedTrip || !this.selectedTrip.eta) return true;
			return (this.selectedTrip.etd && this.eta.date < this.selectedTrip.etd.date) || this.eta.date > this.selectedTrip.eta.date;
		},
		tripNumber() {
			return this.selectedTrip ? this.selectedTrip.tripNumber : '';
		},
		orderNumbers() {
			if (!this.orders) return '';
			return this.orders.map(x => x.orderNumber).join(', ');
		},
		etdTime() {
			if (!this.etd.hasTime) return null;

			return this.etd.date ?
				moment(this.etd.date, storageDateFormat).format('LT') :
				undefined;
		},
		etaTime() {
			if (!this.eta.hasTime) return null;

			return this.eta.date ?
				moment(this.eta.date, storageDateFormat).format('LT') :
				undefined;
		},
		ordersDateTimeValidation() {
			return validateDateTimeForOrdersInTrip(this.orders, this.etd, this.eta);
		},
		ordersDateTimeWarningMessages() {
			if (!this.ordersDateTimeValidation.isInvalid) {
				return { etd: '', eta: '', };
			}
			let errorTypes = { etd: [], eta: [] };
			this.ordersDateTimeValidation.orders.forEach((orderErrorObj, i) => {
				if (orderErrorObj.isInvalid) {
					if (orderErrorObj.orderStartsTooLate) errorTypes.etd.push(this.orders[i].orderNumber);
					if (orderErrorObj.orderEndsTooSoon) errorTypes.eta.push(this.orders[i].orderNumber);
				}
			});
			let errorMessages = {};
			if (errorTypes.etd.length) {
				errorMessages.etd = this.$tc('ordersEtdWarning', errorTypes.etd.length, { orders: formatArray(errorTypes.etd) });
			}
			if (errorTypes.eta.length) {
				errorMessages.eta = this.$tc('ordersEtaWarning', errorTypes.eta.length, { orders: formatArray(errorTypes.eta) });
			}
			return errorMessages;
		},
	},

	methods: {
		formatDateObject(dateObj) {
			if (!dateObj || !dateObj.date) {
				return '';
			}
			return displayDateFormatter(dateObj.date);
		},
		processShown() {
			this.fromLocations = [];
			this.toLocations = [];
			if (this.selectedTrip.fromAddress) {
				this.fromLocations.push({
					label: this.$t('fromTripStart'),
					labelClass: 'stretch-details-label',
					primaryClass: 'stretch-details-primary',
					name: this.selectedTrip.fromAddress.name,
					option: 'trip'
				});
				this.defaultStartIndex = 0;
			}
			if (this.selectedTrip.toAddress) {
				this.toLocations.push({
					label: this.$t('toTripEnd'),
					labelClass: 'stretch-details-label',
					primaryClass: 'stretch-details-primary',
					name: this.selectedTrip.toAddress.name,
					option: 'trip'
				});
				this.defaultEndIndex = 0;
			}

			if (this.selectedTrip.viaLocations && this.selectedTrip.viaLocations.length > 1) {
				this.fromLocations.push({
					label: this.$t('fromViaLocation'),
					labelClass: 'stretch-details-label',
					option: 'via',
					select: true,
					items: this.selectedTrip.viaLocations.map(loc => loc.address),
					selectedItem: this.selectedTrip.viaLocations[0].address
				});
				this.toLocations.unshift({
					label: this.$t('toViaLocation'),
					labelClass: 'stretch-details-label',
					option: 'via',
					select: true,
					items: this.selectedTrip.viaLocations.map(loc => loc.address),
					selectedItem: this.selectedTrip.viaLocations[0].address
				});

			} else if (this.selectedTrip.viaLocations && this.selectedTrip.viaLocations.length === 1) {
				this.fromLocations.push({
					label: this.$t('fromViaLocation'),
					labelClass: 'stretch-details-label',
					primaryClass: 'stretch-details-primary',
					name: this.selectedTrip.viaLocations[0].address.name,
					selectedItem: this.selectedTrip.viaLocations[0],
					option: 'via'
				});
				this.toLocations.unshift({
					label: this.$t('toViaLocation'),
					labelClass: 'stretch-details-label',
					primaryClass: 'stretch-details-primary',
					name: this.selectedTrip.viaLocations[0].address.name,
					selectedItem: this.selectedTrip.viaLocations[0],
					option: 'via'
				});
			}

			if (this.orders && this.orders.length) {
				let fromAddress = this.orders[0].despatchParty || this.orders[0].consignor;
				let sameFromAddress = this.selectedTrip.fromAddress;
				let multipleDespatchAddresses = false;

				for (let o of this.orders) {
					let oAddr = o.despatchParty || o.consignor;
					if (!oAddr) { continue; }

					if (!sameFromAddress || sameFromAddress.postalCode !== oAddr.postalCode
						|| !sameFromAddress.street || !oAddr.street
						|| sameFromAddress.street.toLowerCase() !== oAddr.street.toLowerCase()) {
						sameFromAddress = false;
					}
					if ((this.orders.length > 1 && fromAddress)
						&& (oAddr.postalCode !== fromAddress.postalCode
						|| !fromAddress.street || !oAddr.street
						|| oAddr.street.toLowerCase() !== fromAddress.street.toLowerCase())) {
						multipleDespatchAddresses = true;
					}
					if (!fromAddress) {
						fromAddress = oAddr;
					}
				}

				if (fromAddress && !sameFromAddress) {
					let fromName = multipleDespatchAddresses ? this.$t('multipleLocations') : fromAddress.name;
					this.fromLocations.unshift({
						label: this.$t('fromPickupLocation'),
						labelClass: 'stretch-details-label',
						primaryClass: 'stretch-details-primary',
						name: fromName,
						option: 'despatchParty'
					});
					if (this.selectedTrip.type && this.selectedTrip.type.internalCode && this.selectedTrip.type.internalCode.toUpperCase() === 'L'
						|| (this.orders.length === 1 && this.orders[0].next && this.orders[0].next.length === 0)
					) {
						this.defaultStartIndex = 0;
					} else {
						this.defaultStartIndex = 1;
					}
				}

				let toAddress = this.orders[0].deliveryParty || this.orders[0].consignee;
				let sameToAddress = this.selectedTrip.toAddress;
				let multipleDeliveryAddresses = false;

				for (let o of this.orders) {
					let oAddr = o.deliveryParty || o.consignee;
					if (!oAddr) { continue; }

					if (!sameToAddress || sameToAddress.postalCode !== oAddr.postalCode
						|| !sameToAddress.street || !oAddr.street
						|| sameToAddress.street.toLowerCase() !== oAddr.street.toLowerCase()) {
						sameToAddress = false;
					}
					if ((this.orders.length > 1 && toAddress)
						&& (oAddr.postalCode !== toAddress.postalCode
						|| !toAddress.street || !oAddr.street
						|| oAddr.street.toLowerCase() !== toAddress.street.toLowerCase())) {
						multipleDeliveryAddresses = true;
					}
					if (!toAddress) {
						toAddress = oAddr;
					}
				}
				if (toAddress && !sameToAddress) {
					let toName = multipleDeliveryAddresses ? this.$t('multipleLocations') : toAddress.name;
					this.toLocations.push({
						label: this.$t('toDeliveryLocation'),
						labelClass: 'stretch-details-label',
						primaryClass: 'stretch-details-primary',
						name: toName,
						option: 'deliveryParty'
					});
				}
			}

			this.etd = deepCopy(this.selectedTrip.etd);
			this.eta = deepCopy(this.selectedTrip.eta);
			this.changeFromLocation(this.defaultStartIndex);
			this.changeToLocation(this.defaultEndIndex);
			this.to = this.toLocations.length - 1;
		},
		onClose() {
			this.$emit('close');
			this.resetWindow();
		},
		resetWindow() {
			this.fromLocations = [];
			this.toLocations = [];
			this.customFromLocation = {};
			this.customToLocation = {};
			this.from = 0;
			this.to = 0;
			this.etd = {
				date: '',
				hasTime: false,
				timeZone: 'Europe/Oslo',
				epoch: ''
			};
			this.eta = {
				date: '',
				hasTime: false,
				timeZone: 'Europe/Oslo',
				epoch: ''
			};
			this.allowCustomFrom = false;
			this.allowCustomTo = false;
			this.defaultStartIndex = 0;
			this.defaultEndIndex = 0;
		},
		addToTrip() {
			let despatchOption = this.from >= this.fromLocations.length ? 'custom' : this.fromLocations[this.from].option;
			let fromLocation = this.customFromLocation;
			if (despatchOption === 'via') {
				despatchOption = 'custom';
				fromLocation = this.fromLocations[this.from].selectedItem;
			}

			let deliveryOption = this.to >= this.toLocations.length ? 'custom' : this.toLocations[this.to].option;
			let toLocation = this.customToLocation;
			if (deliveryOption === 'via')
				toLocation = this.toLocations[this.to].selectedItem;

			this.$emit('addToTrip',{
				despatchOption: despatchOption,
				deliveryOption: deliveryOption,
				etd: this.etd,
				eta: this.eta,
				customDespatchParty: fromLocation,
				customDeliveryParty: toLocation
			});
		},
		changeFromLocation(index) {
			this.from = index;
		},
		changeToLocation(index) {
			this.to = index;
		},
		onCustomFromSelected(value) {
			this.customFromLocation = value;
		},
		onCustomToSelected(value) {
			this.customToLocation = value;
		},
		customFromToggle(value) {
			if (value) {
				this.changeFromLocation(this.fromLocations.length);
			} else {
				this.changeFromLocation(this.defaultStartIndex);
			}
		},
		customToToggle(value) {
			if (value) {
				this.changeToLocation(this.toLocations.length);
			} else {
				this.changeToLocation(this.defaultEndIndex);
			}
		},
		onChangeFromViaPlace(fromIndex, value) {
			if (this.fromLocations[fromIndex]) {
				this.fromLocations[fromIndex].selectedItem = value;
				this.from = fromIndex;
			}
		},
		onChangeToViaPlace(toIndex, value) {
			if (this.toLocations[toIndex]) {
				this.toLocations[toIndex].selectedItem = value;
				this.to = toIndex;
			}
		},
		changeEtd({ dateValue, timeValue, hasTime }) {
			let storedDate = this.etd ? this.etd.date : undefined;
			let date = dateValue ? dateValue : storedDate;

			let time = timeValue ?
				moment(timeValue, 'LT').format('HH:mm') :
				moment(storedDate, storageDateFormat).format('HH:mm');

			hasTime = hasTime === null ?
				this.etd.hasTime :
				hasTime;

			this.etd = createDateObject({ date, hasTime, time }, Boolean(dateValue));
		},
		changeEta({ dateValue, timeValue, hasTime }) {
			let storedDate = this.eta ? this.eta.date : undefined;
			let date = dateValue ? dateValue : storedDate;

			let time = timeValue ?
				moment(timeValue, 'LT').format('HH:mm') :
				moment(storedDate, storageDateFormat).format('HH:mm');

			hasTime = hasTime === null ?
				this.eta.hasTime :
				hasTime;

			this.eta = createDateObject({ date, hasTime, time }, Boolean(dateValue));
		}
	},
};
</script>

<style >
.stretch-details-label {
	font-size: 11px;
	font-weight: 500;
}
.stretch-details-primary {
	font-size: 12px;
}
</style>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';

.dialog-mid {
	display: grid;
	column-gap: 20px;
	grid-template-columns: 225px auto;
	margin-top:15px;
	padding-bottom: 20px;
	.custom-switches {
		display: grid;
		grid-row-gap: 10px;
		grid-template-rows: repeat(2, min-content);
		padding-top:20px;
	}
}

.orders-date-time-warning {
	margin: 0px 2px 8px 2px;
	color: get-color($orange, 'base');
}
</style>
